import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import {useSelector} from 'react-redux';

import {ApiContext} from '../../../../api/Api';
import Button from '../../../../elements/CustomButton/CustomButton';
import {NotifierContext} from '../../../../utils/Notifier';

const loadingId =
    '@Technology.DevelopmentRequirementForm.developmentRequirements.create';

const initialForm = {
    title: '',
    systemId: '',
    priorityId: '',
    desiredDate: '',
    businessNeed: '',
    expectedResult: ''
};

const DevelopmentRequirementForm = ({history}) => {
    const api = useContext(ApiContext);
    const notifier = useContext(NotifierContext);

    const systems = useSelector(
        ({api: apiState}) => apiState.developmentSystems || []
    );

    const priorities = useSelector(
        ({api: apiState}) => apiState.developmentPriorities || []
    );

    const loading = useSelector(
        state => Boolean(state.loadingIds[loadingId])
    );

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [cataloguesLoading, setCataloguesLoading] = useState(true);

    useEffect(() => {
        let active = true;

        Promise.all([
            api.developmentSystems.get({
                pageSize: 100,
                filters: {isActive: 1}
            }),
            api.developmentPriorities.get({
                pageSize: 100,
                filters: {isActive: 1}
            })
        ])
            .catch(() => {
                notifier.error(
                    'No fue posible cargar los catálogos del formulario.'
                );
            })
            .finally(() => {
                if (active) {
                    setCataloguesLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [api, notifier]);

    const activeSystems = useMemo(
        () => systems.filter(item => item.isActive !== false),
        [systems]
    );

    const activePriorities = useMemo(
        () => priorities.filter(item => item.isActive !== false),
        [priorities]
    );

    const onChange = ({target}) => {
        const {name, value} = target;

        setForm(current => ({
            ...current,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(current => ({
                ...current,
                [name]: undefined
            }));
        }
    };

    const validate = () => {
        const nextErrors = {};

        if (!form.title.trim()) {
            nextErrors.title = 'Escribe un título para el requerimiento.';
        }

        if (!form.systemId) {
            nextErrors.systemId = 'Selecciona el sistema relacionado.';
        }

        if (!form.priorityId) {
            nextErrors.priorityId = 'Selecciona una prioridad.';
        }

        if (!form.businessNeed.trim()) {
            nextErrors.businessNeed =
                'Describe la necesidad que debe atenderse.';
        }

        if (!form.expectedResult.trim()) {
            nextErrors.expectedResult =
                'Describe el resultado que esperas obtener.';
        }

        setErrors(nextErrors);

        return Object.keys(nextErrors).length === 0;
    };

    const save = () => {
        if (loading || !validate()) {
            return;
        }

        const properties = {
            title: form.title.trim(),
            system: `/api/development_systems/${form.systemId}`,
            priority: `/api/development_priorities/${form.priorityId}`,
            businessNeed: form.businessNeed.trim(),
            expectedResult: form.expectedResult.trim()
        };

        if (form.desiredDate) {
            properties.desiredDate = form.desiredDate;
        }

        api.developmentRequirements.create({
            properties,
            loadingId
        })
            .then(() => {
                notifier.success(
                    'El requerimiento se guardó correctamente como Borrador.'
                );

                history.push('/technology/development-requests');
            })
            .catch(() => {
                notifier.error(
                    'No fue posible guardar el requerimiento. Revisa la información e inténtalo nuevamente.'
                );
            });
    };

    const cancel = () => {
        if (loading) {
            return;
        }

        history.push('/technology/development-requests');
    };

    const fieldClass = name =>
        `form-group ${errors[name] ? 'has-error' : ''}`;

    return (
        <div className="container-fluid main-container">
            <div
                style={{
                    maxWidth: '1050px',
                    margin: '0 auto',
                    paddingBottom: '40px'
                }}
            >
                <div
                    className="clearfix"
                    style={{
                        marginBottom: '22px',
                        borderBottom: '1px solid #e5e5e5',
                        paddingBottom: '18px'
                    }}
                >
                    <div className="pull-left">
                        <h2 style={{marginTop: 0, marginBottom: '6px'}}>
                            Nuevo requerimiento
                        </h2>

                        <p
                            className="text-muted"
                            style={{marginBottom: 0}}
                        >
                            Define claramente la necesidad y el resultado
                            esperado. El requerimiento iniciará como
                            Borrador.
                        </p>
                    </div>

                    <div className="pull-right">
                        <span
                            className="label label-default"
                            style={{
                                display: 'inline-block',
                                fontSize: '13px',
                                padding: '8px 14px',
                                borderRadius: '16px'
                            }}
                        >
                            Folio automático
                        </span>
                    </div>
                </div>

                <div
                    className="panel panel-default"
                    style={{
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                    }}
                >
                    <div
                        className="panel-heading"
                        style={{
                            background: '#f8f9fb',
                            borderRadius: '8px 8px 0 0',
                            padding: '16px 20px'
                        }}
                    >
                        <h4 style={{margin: 0}}>
                            <i
                                className="fa fa-info-circle"
                                style={{marginRight: '9px'}}
                            />
                            Información general
                        </h4>
                    </div>

                    <div
                        className="panel-body"
                        style={{padding: '24px'}}
                    >
                        <div className={fieldClass('title')}>
                            <label htmlFor="title">
                                Título del requerimiento *
                            </label>

                            <input
                                id="title"
                                name="title"
                                type="text"
                                className="form-control"
                                value={form.title}
                                maxLength={255}
                                disabled={loading}
                                onChange={onChange}
                                placeholder="Ej. Automatizar el proceso de autorización de solicitudes"
                            />

                            <div className="clearfix">
                                {errors.title ? (
                                    <span className="help-block pull-left">
                                        {errors.title}
                                    </span>
                                ) : null}

                                <small className="text-muted pull-right">
                                    {form.title.length}/255
                                </small>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-5">
                                <div className={fieldClass('systemId')}>
                                    <label htmlFor="systemId">
                                        Sistema relacionado *
                                    </label>

                                    <select
                                        id="systemId"
                                        name="systemId"
                                        className="form-control"
                                        value={form.systemId}
                                        disabled={
                                            loading ||
                                            cataloguesLoading
                                        }
                                        onChange={onChange}
                                    >
                                        <option value="">
                                            Selecciona un sistema
                                        </option>

                                        {activeSystems.map(system => (
                                            <option
                                                key={system.id}
                                                value={system.id}
                                            >
                                                {system.name}
                                            </option>
                                        ))}
                                    </select>

                                    {errors.systemId ? (
                                        <span className="help-block">
                                            {errors.systemId}
                                        </span>
                                    ) : null}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className={fieldClass('priorityId')}>
                                    <label htmlFor="priorityId">
                                        Prioridad *
                                    </label>

                                    <select
                                        id="priorityId"
                                        name="priorityId"
                                        className="form-control"
                                        value={form.priorityId}
                                        disabled={
                                            loading ||
                                            cataloguesLoading
                                        }
                                        onChange={onChange}
                                    >
                                        <option value="">
                                            Selecciona una prioridad
                                        </option>

                                        {activePriorities.map(priority => (
                                            <option
                                                key={priority.id}
                                                value={priority.id}
                                            >
                                                {priority.name}
                                            </option>
                                        ))}
                                    </select>

                                    {errors.priorityId ? (
                                        <span className="help-block">
                                            {errors.priorityId}
                                        </span>
                                    ) : null}
                                </div>
                            </div>

                            <div className="col-md-3">
                                <div className="form-group">
                                    <label htmlFor="desiredDate">
                                        Fecha deseada
                                    </label>

                                    <input
                                        id="desiredDate"
                                        name="desiredDate"
                                        type="date"
                                        className="form-control"
                                        value={form.desiredDate}
                                        disabled={loading}
                                        onChange={onChange}
                                    />

                                    <span className="help-block">
                                        Opcional
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            className="alert alert-info"
                            style={{
                                marginTop: '10px',
                                marginBottom: 0,
                                borderRadius: '6px'
                            }}
                        >
                            <i
                                className="fa fa-lock"
                                style={{marginRight: '8px'}}
                            />
                            El folio, estado inicial y datos de auditoría
                            serán asignados automáticamente por ALMA.
                        </div>
                    </div>
                </div>

                <div
                    className="panel panel-default"
                    style={{
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                    }}
                >
                    <div
                        className="panel-heading"
                        style={{
                            background: '#f8f9fb',
                            borderRadius: '8px 8px 0 0',
                            padding: '16px 20px'
                        }}
                    >
                        <h4 style={{margin: 0}}>
                            <i
                                className="fa fa-briefcase"
                                style={{marginRight: '9px'}}
                            />
                            Necesidad del negocio
                        </h4>
                    </div>

                    <div
                        className="panel-body"
                        style={{padding: '24px'}}
                    >
                        <div className={fieldClass('businessNeed')}>
                            <label htmlFor="businessNeed">
                                ¿Qué problema o necesidad debe atenderse? *
                            </label>

                            <p className="text-muted">
                                Describe la situación actual, quiénes se ven
                                afectados y por qué es importante resolverla.
                            </p>

                            <textarea
                                id="businessNeed"
                                name="businessNeed"
                                className="form-control"
                                rows={7}
                                maxLength={10000}
                                value={form.businessNeed}
                                disabled={loading}
                                onChange={onChange}
                                placeholder="Explica qué sucede actualmente, sus consecuencias y el impacto en la operación..."
                                style={{resize: 'vertical'}}
                            />

                            <div className="clearfix">
                                {errors.businessNeed ? (
                                    <span className="help-block pull-left">
                                        {errors.businessNeed}
                                    </span>
                                ) : null}

                                <small className="text-muted pull-right">
                                    {form.businessNeed.length}/10000
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="panel panel-default"
                    style={{
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                    }}
                >
                    <div
                        className="panel-heading"
                        style={{
                            background: '#f8f9fb',
                            borderRadius: '8px 8px 0 0',
                            padding: '16px 20px'
                        }}
                    >
                        <h4 style={{margin: 0}}>
                            <i
                                className="fa fa-check-circle"
                                style={{marginRight: '9px'}}
                            />
                            Resultado esperado
                        </h4>
                    </div>

                    <div
                        className="panel-body"
                        style={{padding: '24px'}}
                    >
                        <div className={fieldClass('expectedResult')}>
                            <label htmlFor="expectedResult">
                                ¿Cómo sabremos que el requerimiento quedó
                                correctamente atendido? *
                            </label>

                            <p className="text-muted">
                                Define el resultado funcional esperado de
                                manera concreta y verificable.
                            </p>

                            <textarea
                                id="expectedResult"
                                name="expectedResult"
                                className="form-control"
                                rows={7}
                                maxLength={10000}
                                value={form.expectedResult}
                                disabled={loading}
                                onChange={onChange}
                                placeholder="Describe el comportamiento esperado, entregables o criterios que deberán cumplirse..."
                                style={{resize: 'vertical'}}
                            />

                            <div className="clearfix">
                                {errors.expectedResult ? (
                                    <span className="help-block pull-left">
                                        {errors.expectedResult}
                                    </span>
                                ) : null}

                                <small className="text-muted pull-right">
                                    {form.expectedResult.length}/10000
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="clearfix"
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e5e5e5',
                        borderRadius: '8px',
                        padding: '18px 20px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                    }}
                >
                    <Button
                        bsStyle="default"
                        className="pull-left"
                        disabled={loading}
                        onClick={cancel}
                    >
                        <i
                            className="fa fa-times"
                            style={{marginRight: '7px'}}
                        />
                        Cancelar
                    </Button>

                    <Button
                        bsStyle="primary"
                        className="pull-right"
                        disabled={loading || cataloguesLoading}
                        onClick={save}
                    >
                        <i
                            className={
                                loading
                                    ? 'fa fa-spinner fa-spin'
                                    : 'fa fa-save'
                            }
                            style={{marginRight: '7px'}}
                        />
                        {loading
                            ? 'Guardando...'
                            : 'Guardar borrador'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DevelopmentRequirementForm;
